/**
 * AssemblerClient - Core client-side merge logic for all Node engines
 * Port of csharp/AssemblerQuality/ConfigClient/AssemblerClient.cs
 *
 * Uses ConfigClient (IConfig) with API JSON response data to merge templates
 * client-side via the built-in engines (Normal, PreProcess) and, through the optional
 * IEngineCatalog, any catalog engine (NormalJson, PreProcessJson). AssemblerFlex (Flex/Flat)
 * exists only in C#.
 *
 * Additional Node-only helpers (fetchTemplates, getComponentsServer) support
 * the browser bundles and have no C# equivalent — they live here because the
 * browser bundles re-export from this file.
 */

import { EngineNormal, EnginePreProcess, LoaderNormal, LoaderPreProcess } from '@arshu/assembler';
import type { ILoaderNormal, ILoaderPreProcess, MergeResult } from '@arshu/assembler';
import { JsonObject, JsonUtility } from '@arshu/assembler/app';
import { ConfigClient } from '@arshu/assembler/config';
import { PhaseTiming, TimingStage } from '@arshu/assembler';
import type { IEngineCatalog } from './iEngineCatalog.js';

type PlainJsonObject = { [key: string]: unknown };
type PlainJsonMap = globalThis.Map <string, unknown>;

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body: string;
}

interface FetchResponse {
    ok: boolean;
    status: number;
    text(): Promise<string>;
    json(): Promise<unknown>;
}

type FetchFn = (url: string, options: FetchOptions) => Promise<FetchResponse>;

interface ApiTemplatesResponse {
    ContextJson?: PlainJsonObject;
    contextJson?: PlainJsonObject;
    RawData?: Record<string, Record<string, JsonObject>>;
    rawData?: Record<string, Record<string, JsonObject>>;
    [key: string]: unknown;
}

interface ComponentsServerResponse {
    ComponentHtml?: string;
    ComponentId?: string;
    [key: string]: unknown;
}

/**
 * Client-side merge result wrapping the full MergeResult.
 */
export class ClientResult {
    result: MergeResult;

    constructor(result: MergeResult) {
        this.result = result;
    }
}

export class AssemblerClient {
    static async fetchTemplates(
        fetchFn: FetchFn,
        appSite: string,
        appView: string,
        engineType: string,
        mergeMode: 'ClientJs' | 'ClientWasm'
    ): Promise<ApiTemplatesResponse> {
        const response = await fetchFn('/api/get-templates', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                appSite: appSite,
                appView: appView || '',
                engineType: engineType,
                mergeMode: mergeMode
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error('HTTP error! status: ' + response.status + ' - ' + errorText);
        }

        return await response.json() as ApiTemplatesResponse;
    }

    static async getComponentsServer(
        fetchFn: FetchFn,
        appSite: string,
        appView: string,
        engineType: string,
        componentName: string,
        jsonConfig: JsonObject | null = null,
        publishMode: string | null = null
    ): Promise<ComponentsServerResponse> {
        const requestBody = new JsonObject();
        requestBody.set('appSite', appSite);
        requestBody.set('appView', appView || '');
        requestBody.set('engineType', engineType);
        requestBody.set('refreshComponent', componentName);
        requestBody.set('jsonConfig', jsonConfig || new JsonObject());
        // Connected => the server broadcasts the merged component to other connected clients. Only send
        // the field when non-default so a plain Self merge keeps the previous request shape (mirrors
        // performServerGroupRefresh, the Loader's Connected path).
        if (publishMode && publishMode !== 'Self') {
            requestBody.set('publishMode', publishMode);
        }
        const response = await fetchFn('/api/get-components', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JsonUtility.serializeObject(requestBody)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error('HTTP error! status: ' + response.status + ' - ' + errorText);
        }

        return await response.json() as ComponentsServerResponse;
    }

    /**
     * Client-side merge using templates from an API response. Creates a ConfigClient from the API
     * response, selects the loader+engine for engineType, restores replay state, then merges. The
     * client loader build and the client merge are the "apiclient" lane's load and merge scopes on
     * timing.
     */
    static getComponentsClient(
        apiResponse: ApiTemplatesResponse,
        appSite: string,
        appView: string,
        engineType: string,
        timing: PhaseTiming,
        configJson: JsonObject | null = null,
        deliveredClientData: JsonObject | null = null,
        engineCatalog: IEngineCatalog | null = null
    ): ClientResult {
        const baseContextJson = apiResponse.ContextJson || apiResponse.contextJson || {};
        const contextJson = AssemblerClient.prepareContextJson(
            baseContextJson,
            appSite,
            appView || '',
            engineType
        );

        const actionMode = AssemblerClient.extractActionMode(configJson);
        const clientConfig = new ConfigClient(JsonObject.fromPlainObject(apiResponse as unknown as Parameters<typeof JsonObject.fromPlainObject>[0]));
        // Route the server's delivered client-data islands (MergeResult.dynamicClientData) into the client
        // config so the clientMode re-merge below prepends each component's island into the delivered HTML and
        // the Client Handler consumes it — reproducing the server output for client-data scenarios.
        clientConfig.dynamicClientData = deliveredClientData;

        let result: MergeResult;
        if (engineType === 'PreProcess') {
            const loader = timing.within('apiclient', 'PreProcess', TimingStage.Load,
                () => new LoaderPreProcess(clientConfig, appSite, timing)) as unknown as ILoaderPreProcess;
            const engine = new EnginePreProcess(loader);
            result = timing.within('apiclient', 'PreProcess', TimingStage.Merge, () => engine.mergeTemplates(
                appSite, appView || '', true, true, contextJson, timing, configJson, actionMode, true));
        } else if (engineType === 'Normal') {
            const loader = timing.within('apiclient', 'Normal', TimingStage.Load,
                () => new LoaderNormal(clientConfig, appSite, timing)) as unknown as ILoaderNormal;
            const engine = new EngineNormal(loader);
            result = timing.within('apiclient', 'Normal', TimingStage.Merge, () => engine.mergeTemplates(
                appSite, appView || '', true, true, contextJson, timing, configJson, actionMode, true));
        } else {
            const catalogEngine = timing.within('apiclient', engineType, TimingStage.Load,
                () => engineCatalog?.CreateEngine(engineType, clientConfig, appSite, null, null, timing));
            if (!catalogEngine) {
                throw new Error(`No client engine is registered for '${engineType}'.`);
            }
            result = timing.within('apiclient', engineType, TimingStage.Merge, () => catalogEngine.createMergeEngine().mergeTemplates(
                appSite, appView || '', true, true, contextJson, timing, configJson, actionMode, true));
        }

        return new ClientResult(result);
    }

    static prepareContextJson(
        baseContextJson: PlainJsonObject | PlainJsonMap,
        appSite: string,
        appView: string,
        engineType: string
    ): JsonObject {
        const contextJson = new JsonObject();

        if (baseContextJson) {
            if (baseContextJson instanceof Map) {
                for (const [key, value] of baseContextJson) {
                    contextJson.set(key as string, value);
                }
            } else {
                for (const key in baseContextJson) {
                    if (Object.prototype.hasOwnProperty.call(baseContextJson, key)) {
                        contextJson.set(key, baseContextJson[key]);
                    }
                }
            }
        }

        contextJson.set('AppSite', appSite);
        contextJson.set('AppView', appView || '');
        contextJson.set('EngineType', engineType);
        return contextJson;
    }

    static extractActionMode(configJson: JsonObject | null): boolean {
        if (!configJson) {
            return false;
        }

        if (configJson.has('ActionMode')) {
            const actionModeValue = configJson.get('ActionMode');
            if (actionModeValue && actionModeValue.isBool) {
                return actionModeValue.getBool();
            } else if (actionModeValue && actionModeValue.isString && actionModeValue.asString() === 'true') {
                return true;
            }
        }

        return false;
    }

}
